'use client'

import { useEffect, useRef } from 'react'

import { Divider } from 'fukku/Divider/Divider'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useChatBotMessages } from '../../../../hooks/useChatBotMessages'
import {
	type ChatbotMessage,
	MessageType,
} from '../../../../types/messageTypes'
import { AboutAIMessage } from '../../../AboutAIMessage/AboutAIMessage'
import { ConversationItem } from '../../../ConversationItem/ConversationItem'
import { MessageLoader } from '../../../MessageLoader/MessageLoader'
import styles from './ChatDialogContent.module.scss'

export function ChatDialogContent() {
	const { chatHistoryMessages, isLoading } = useChatBotMessages()
	const { t } = useLabels()

	const messagesContainerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		// Scroll to the end of the messages list
		if (messagesContainerRef.current) {
			messagesContainerRef.current.scrollTo?.({
				top: messagesContainerRef.current.scrollHeight,
				behavior: 'smooth',
			})
		}
	}, [chatHistoryMessages])

	const isTimeoutAgentMessageFromAIText = (
		chatHistoryMessage: ChatbotMessage
	) =>
		chatHistoryMessage.type === MessageType.AIText &&
		chatHistoryMessage.isTimeoutAgentMessage

	const isLastAgentMessageText = (chatHistoryMessage: ChatbotMessage) =>
		chatHistoryMessage.type === MessageType.AgentText &&
		chatHistoryMessage.isLastAgentMessage

	const isFirstAgentMessageText = (chatHistoryMessage: ChatbotMessage) =>
		chatHistoryMessage.type === MessageType.AgentText &&
		chatHistoryMessage.isFirstAgentMessage

	return (
		<>
			<AboutAIMessage />
			<div className={styles.chatDialogContent} ref={messagesContainerRef}>
				{chatHistoryMessages.map((chatHistoryMessage, key) => (
					<div
						key={`${chatHistoryMessage.role}-${key}`}
						className={styles.chatHistoryMessage}
					>
						{isFirstAgentMessageText(chatHistoryMessage) && (
							<Divider
								title={t('assistant.handoff.start.title')}
								variant='subtle'
							/>
						)}
						<ConversationItem message={chatHistoryMessage} />
						{(isTimeoutAgentMessageFromAIText(chatHistoryMessage) ||
							isLastAgentMessageText(chatHistoryMessage)) && (
							<Divider
								title={t('assistant.handoff.end.title')}
								variant='subtle'
							/>
						)}
					</div>
				))}
				{isLoading && <MessageLoader isLoading />}
			</div>
		</>
	)
}
